import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Activity, AlertTriangle, List, Brain, Database } from 'lucide-react'
import { LiveCollectionMonitor } from '@/components/monitoring/tabs/LiveCollectionMonitor'
import { JobsExplorer, JobsFilter } from '@/components/monitoring/tabs/JobsExplorer'
import { Errors } from '@/components/monitoring/tabs/Errors'
import { BehaviorLearning } from '@/components/monitoring/tabs/BehaviorLearning'
import { RecsPivot } from '@/components/monitoring/tabs/RecsPivot'

/**
 * Monitoring Dashboard - Main Page
 *
 * Page for monitoring Cube data pipeline operations.
 * Provides real-time visibility into collections, failures, and system health.
 *
 * @see docs/MONITORING_DASHBOARD_ARCHITECTURE.md
 */
const VALID_TABS = ['live', 'jobs', 'errors', 'recs', 'behavior'] as const

export function MonitoringPage() {
  const { currentOrganization } = useOrganization()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam && VALID_TABS.includes(tabParam as typeof VALID_TABS[number]) ? tabParam : 'live'
  })
  const [jobsFilter, setJobsFilter] = useState<JobsFilter | null>(null)

  // Get organization ID for filtering
  const organizationId = currentOrganization?.id

  // Update URL when tab changes
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab)
    const newParams = new URLSearchParams(searchParams)
    if (tab === 'live') {
      newParams.delete('tab')
    } else {
      newParams.set('tab', tab)
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // Sync tab when URL changes (e.g., browser back button)
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const newTab = tabParam && VALID_TABS.includes(tabParam as typeof VALID_TABS[number]) ? tabParam : 'live'
    if (newTab !== activeTab) {
      setActiveTab(newTab)
    }
  }, [searchParams])

  // Handle smart filter from performance table - switch to jobs tab with filter
  const handleSmartFilter = (filter: JobsFilter) => {
    setJobsFilter(filter)
    handleTabChange('jobs')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Cube Monitoring
        </h1>
        <p className="text-muted-foreground">
          Real-time monitoring and management of data collection pipelines.
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="live" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Live Monitor</span>
          </TabsTrigger>
          <TabsTrigger value="jobs" className="flex items-center gap-2">
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Jobs</span>
          </TabsTrigger>
          <TabsTrigger value="errors" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">Errors</span>
          </TabsTrigger>
          <TabsTrigger value="recs" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Recs</span>
          </TabsTrigger>
          <TabsTrigger value="behavior" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            <span className="hidden sm:inline">Behavior</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Live Collection Monitor */}
        <TabsContent value="live" className="space-y-4">
          <LiveCollectionMonitor onSmartFilter={handleSmartFilter} organizationId={organizationId} />
        </TabsContent>

        {/* Tab 2: Jobs Explorer */}
        <TabsContent value="jobs" className="space-y-4">
          <JobsExplorer
            initialFilter={jobsFilter}
            onFilterClear={() => setJobsFilter(null)}
            organizationId={organizationId}
          />
        </TabsContent>

        {/* Tab 3: Errors */}
        <TabsContent value="errors" className="space-y-4">
          <Errors organizationId={organizationId} />
        </TabsContent>

        {/* Tab 4: Records Pivot */}
        <TabsContent value="recs" className="space-y-4">
          <RecsPivot organizationId={organizationId} />
        </TabsContent>

        {/* Tab 5: Behavior Learning */}
        <TabsContent value="behavior" className="space-y-4">
          <BehaviorLearning onSmartFilter={handleSmartFilter} organizationId={organizationId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
